// widget/src/components/FinMessage.tsx
//
// Message renderer for the "Pearl — Messenger" variant. Same data shape as
// MessageCard, but rendered in the clean light-bubble style. Attachment
// cards are reused as-is (they carry their own styling).

import type React from "react"
import { memo } from "react"
import type { Message, MessageAttachment } from "../types"
import { cn } from "../util"
import fm from "../styles/widgetFin.module.css"
import { cleanForDisplay } from "./MessageCard"
import { ProductCard } from "./ProductCard"
import { ColorCard } from "./ColorCard"
import { TrackingCard } from "./TrackingCard"
import { DiscountChip } from "./DiscountChip"
import { EmailCaptureForm } from "./EmailCaptureForm"
import { VideoCard } from "./VideoCard"
import { BuildStepCard } from "./BuildStepCard"
import { BuildSummaryCard } from "./BuildSummaryCard"

/** True when a product card carries a usable price. */
function hasPrice(p?: string): boolean {
  return p != null && String(p).trim() !== ""
}

/**
 * Collapse duplicate product cards (same handle) down to one, preferring the
 * card that has a price. Non-product attachments pass through untouched and
 * original order is preserved. Fixes Pearl emitting the same product as both
 * a priced and a price-less card.
 */
function dedupeProductCards(atts: MessageAttachment[]): MessageAttachment[] {
  const indexByHandle = new Map<string, number>()
  const out: MessageAttachment[] = []
  for (const att of atts) {
    if (att.kind !== "product") {
      out.push(att)
      continue
    }
    const key = (att.data.handle || att.data.productUrl || att.data.name || "")
      .split("?")[0]
      .toLowerCase()
    if (!key) {
      out.push(att)
      continue
    }
    const existingIdx = indexByHandle.get(key)
    if (existingIdx === undefined) {
      indexByHandle.set(key, out.length)
      out.push(att)
    } else {
      const existing = out[existingIdx]
      // Replace a price-less kept card with this priced duplicate; otherwise
      // drop the duplicate.
      if (
        existing.kind === "product" &&
        !hasPrice(existing.data.price) &&
        hasPrice(att.data.price)
      ) {
        out[existingIdx] = att
      }
    }
  }
  return out
}

interface Props {
  message: Message
  onSubmitText?: (text: string) => void
  onSubmitEmail?: (email: string) => void
  /** Direct add-to-Shopify-cart (opens the cart) for the inline build-summary
   *  card — bypasses the hidden chat cart. */
  onAddToShopifyCart?: (variantId: number) => void
}

function FinMessageImpl({
  message,
  onSubmitText,
  onSubmitEmail,
  onAddToShopifyCart,
}: Props) {
  if (message.role === "user") {
    const images = message.images ?? []
    return (
      <div className={cn(fm.row, fm["row-user"])}>
        <div
          className={cn(
            fm.bubble,
            fm["bubble-user"],
            images.length > 0 && fm["bubble-has-media"],
          )}
        >
          {images.length > 0 && (
            <div className={fm["bubble-images"]}>
              {images.map((src) => (
                <img
                  key={src}
                  className={fm["bubble-image"]}
                  src={src}
                  alt="You shared"
                  loading="lazy"
                />
              ))}
            </div>
          )}
          {message.text && (
            <span className={fm["bubble-text"]}>{message.text}</span>
          )}
        </div>
      </div>
    )
  }

  const pendingTools = (message.tools ?? []).filter((t) => t.status === "pending")
  // Dedup product cards by handle, keeping the one that has a price. Pearl
  // sometimes emits the same product twice — e.g. a priced show_product_card
  // AND a price-less one (caught with "Luna One Wheel Kits": two cards, only
  // one had $119.15). Showing both is confusing; we keep the priced card.
  const attachments = dedupeProductCards(message.attachments ?? [])
  // Suppress inline suggestion chips when this turn drove the build-kit
  // flow — the card (inline OR in the canvas) already renders the
  // canonical chips, so rendering them inline too produced a confusing
  // duplicate chip strip. Two signals to catch both routing modes:
  //   1) attachment-based: the build card is inlined under the bubble.
  //   2) tool-based: when canvas routing is on, build_step/summary go to
  //      the canvas instead of attachments, so attachments never matches.
  //      The build_car_kit tool call still lands on `message.tools` in
  //      both cases, so suppress whenever it appears.
  const hasBuildAttachment = attachments.some(
    (a) => a.kind === "build_step" || a.kind === "build_summary",
  )
  const hasBuildTool = (message.tools ?? []).some(
    (t) => t.name === "build_car_kit",
  )
  let chips = hasBuildAttachment || hasBuildTool ? [] : message.chips ?? []
  // After a color card (agent-sent OR Pearl-sent), always offer the sizing
  // next-step — "found the color" → "how much do I need". Prepended (and
  // deduped) so it shows even if the chip strip caps the count. Justin 2026-06-05.
  const hasColorAttachment = attachments.some((a) => a.kind === "color")
  if (hasColorAttachment) {
    const sizingChip = "How much do I need?"
    chips = [
      sizingChip,
      ...chips.filter((c) => c.toLowerCase() !== sizingChip.toLowerCase()),
    ]
  }

  // Navigation chips — surfaced when Pearl shows a `product` attachment
  // with a real productUrl. Tap the chip → page navigates to the product
  // in the same tab. The chat resumes automatically on the new page via
  // the session-resume handshake, so Pearl stays open + the conversation
  // continues. Truncate long product names so the chip stays one line.
  // Skipped for `products` (plural) — those have multiple options, the
  // customer picks via the per-card "View product →" link. Skipped for
  // build flows (BuildSummaryCard owns the navigation surface). Caught
  // 2026-06-03 — Justin asked for a "Show me / Take me to it" chip.
  const navChips: Array<{ label: string; url: string }> = []
  if (!hasBuildAttachment) {
    for (const a of attachments) {
      if (a.kind !== "product") continue
      const url = a.data?.productUrl
      if (!url) continue
      const name = a.data?.name ?? "product"
      const short = name.length > 36 ? name.slice(0, 33).trimEnd() + "…" : name
      navChips.push({ label: `Take me to ${short}`, url })
    }
  }

  const displayText = cleanForDisplay(message.text)

  const noContent =
    !displayText && pendingTools.length === 0 && attachments.length === 0
  if (noContent) {
    return chips.length > 0 || navChips.length > 0 ? (
      <Chips chips={chips} navChips={navChips} onPick={onSubmitText} />
    ) : null
  }

  return (
    <>
      <div className={cn(fm.row, fm["row-assistant"])}>
        <div className={cn(fm.bubble, fm["bubble-assistant"])}>
          {/*
            Real-human takeover indicator. When `senderName` is set, the row
            came from chat_messages.role='agent' (a teammate replying from the
            staff dashboard, not Pearl AI). Inlined inside the bubble so it
            doesn't disrupt the row's flex layout — see widgetFin.module.css's
            .row rule which uses display:flex with the bubble as the sole
            flex child.

            Label format: drop the "from DipYourCar" suffix when senderName
            already carries the brand (e.g. "DYC Team") to avoid the
            "DYC Team from DipYourCar" double-brand awkwardness.
          */}
          {message.senderName && (
            <div
              style={{
                fontSize: "11px",
                opacity: 0.65,
                marginBottom: "6px",
                fontWeight: 500,
              }}
            >
              {/DYC|DipYourCar/i.test(message.senderName)
                ? message.senderName
                : `${message.senderName} from DipYourCar`}
            </div>
          )}
          {displayText}
          {message.pending && displayText && <span className={fm.caret}>▍</span>}
          {pendingTools.map((t) => (
            <div key={t.id} className={fm["tool-line"]}>
              {humanizeTool(t.name)}…
            </div>
          ))}
          {attachments.length > 0 && (
            <div className={fm.attachments}>
              {attachments.map((att, i) => {
                // Wrapper class controls image aspect: product/color images are
                // square (they were cropped to a squished strip by the shared
                // 88px card style), video thumbnails stay 16:9, everything else
                // is full-width.
                let card: React.ReactNode = null
                let cls = fm["att-wide"]
                switch (att.kind) {
                  case "product":
                    card = <ProductCard data={att.data} />
                    cls = fm["att-media"]
                    break
                  case "color":
                    card = <ColorCard data={att.data} />
                    cls = fm["att-media"]
                    break
                  case "video":
                    card = <VideoCard data={att.data} />
                    cls = fm["att-video"]
                    break
                  case "tracking":
                    card = <TrackingCard data={att.data} />
                    break
                  case "discount":
                    card = <DiscountChip data={att.data} />
                    break
                  case "email_capture":
                    card = (
                      <EmailCaptureForm onSubmit={(e) => onSubmitEmail?.(e)} />
                    )
                    break
                  case "build_step":
                    card = (
                      <BuildStepCard
                        data={att.data}
                        onChipClick={(label) => onSubmitText?.(label)}
                      />
                    )
                    break
                  case "build_summary":
                    card = (
                      <BuildSummaryCard
                        data={att.data}
                        onAddToCart={(kitVariantId, addOnNames) => {
                          // Kit → straight into the Shopify cart + open the
                          // cart. Add-ons (product ids) go via Pearl.
                          onAddToShopifyCart?.(kitVariantId)
                          if (addOnNames.length > 0)
                            onSubmitText?.(
                              `Also add ${addOnNames.join(" + ")} to my cart`,
                            )
                        }}
                        onUserReply={(text) => onSubmitText?.(text)}
                      />
                    )
                    break
                  case "image":
                    // Agent-sent image — rendered as a real <img> so it
                    // survives cleanForDisplay (which strips markdown image
                    // syntax). Rounded, max-width constrained, lazy-loaded to
                    // match the bubble's lightweight feel.
                    card = (
                      <img
                        src={att.data.url}
                        alt={att.data.alt ?? ""}
                        loading="lazy"
                        style={{
                          display: "block",
                          maxWidth: "100%",
                          width: "auto",
                          borderRadius: 10,
                          marginTop: 2,
                        }}
                      />
                    )
                    cls = fm["att-wide"]
                    break
                  case "messaging_handoff": {
                    // Branded card: "Prefer to text? Continue where we left
                    // off." + a WhatsApp button (green) and an iMessage button
                    // (blue). Only render the button whose url is present.
                    // Styled via .handoff-* classes in widgetFin.module.css.
                    const { whatsappUrl, imessageUrl } = att.data
                    if (!whatsappUrl && !imessageUrl) return null
                    card = (
                      <div className={fm["handoff-card"]}>
                        <p className={fm["handoff-title"]}>
                          Prefer to text? Continue where we left off.
                        </p>
                        <div className={fm["handoff-actions"]}>
                          {imessageUrl && (
                            <a
                              href={imessageUrl}
                              className={`${fm["handoff-btn"]} ${fm["handoff-btn-imessage"]}`}
                            >
                              Continue on iMessage
                            </a>
                          )}
                          {whatsappUrl && (
                            <a
                              href={whatsappUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`${fm["handoff-btn"]} ${fm["handoff-btn-whatsapp"]}`}
                            >
                              Continue on WhatsApp
                            </a>
                          )}
                        </div>
                      </div>
                    )
                    cls = fm["att-wide"]
                    break
                  }
                  default:
                    return null
                }
                return (
                  <div key={i} className={cls}>
                    {card}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
      {(chips.length > 0 || navChips.length > 0) && (
        <Chips chips={chips} navChips={navChips} onPick={onSubmitText} />
      )}
    </>
  )
}

export function Chips({
  chips,
  navChips,
  onPick,
}: {
  chips: string[]
  /** Navigation chips that ROUTE the page on tap instead of sending a
   *  follow-up customer message. Surfaced by FinMessage when Pearl shows
   *  a product/tracking attachment with a real URL — the customer can
   *  jump to the product page in one tap without having to type. Pearl
   *  + the chat thread persist across the same-tab navigation thanks to
   *  the session-resume work. Caught 2026-06-03 when Justin asked for a
   *  "Show me / Take me to it" chip after Pearl surfaces a product. */
  navChips?: Array<{ label: string; url: string }>
  onPick?: (text: string) => void
}) {
  if (!chips.length && !(navChips && navChips.length)) return null
  return (
    <div className={fm.chips}>
      {(navChips ?? []).map((nc) => (
        <a
          key={`nav-${nc.url}`}
          href={nc.url}
          className={`${fm.chip} ${fm["chip-nav"]}`}
        >
          {nc.label} &rarr;
        </a>
      ))}
      {chips.map((c) => (
        <button
          key={c}
          type="button"
          className={fm.chip}
          onClick={() => onPick?.(c)}
        >
          {c}
        </button>
      ))}
    </div>
  )
}

function humanizeTool(name: string): string {
  switch (name) {
    case "track_order":
      return "Looking up your order"
    case "find_color":
      return "Finding that color"
    case "search_products":
      return "Checking the catalog"
    case "show_product_card":
      return "Pulling up the product"
    case "search_knowledge":
      return "Checking our guides"
    case "get_size_guide":
      return "Checking sizing"
    case "decode_vehicle":
      return "Looking up your vehicle"
    case "navigate_product":
      return "Opening the page"
    case "add_to_cart":
      return "Adding to your cart"
    case "offer_discount":
      return "Checking discounts"
    case "capture_email":
      return "Noting your email"
    case "handoff_to_agent":
      return "Grabbing a teammate"
    case "get_customer_context":
      return "Checking your history"
    default:
      return "Checking"
  }
}

/** Memoized: messages are immutable rows and the parent re-renders on every
 *  composer keystroke — without memo, typing re-rendered every bubble, card,
 *  and gallery in the thread (the 2026-06-12 "super laggy typing" report).
 *  Callback props must be referentially stable (see FinChatWidget). */
export const FinMessage = memo(FinMessageImpl)
